import React from 'react';
import { Box, Flex, Text } from 'rebass';
import StatusChip from '@/components/Status/StatusChip';
import StatusDot from '@/components/Status/StatusDot';
import { HazardAnswer } from './types';
import ButtonToggle from '@/components/Button/ButtonToggle';
import ReactSelect from '@/components/ReactSelect';

export type HazardResultRow = {
  id: string;
  name: string;
  hazardLevel: 'LOW' | 'HIGH';
  answer: HazardAnswer | null;
  isProtected: HazardAnswer | null;
  backflowAssemblyId: string | null;
  removed?: boolean;
};

type Assembly = {
  id: string;
  serialNumber?: string | null;
  model?: {
    name?: string | null;
    backflowAssemblyMake?: { name?: string | null } | null;
  } | null;
};

const HAZARD_OPTIONS = [
  { label: 'Yes', value: 'YES' as HazardAnswer },
  { label: 'No', value: 'NO' as HazardAnswer },
  { label: 'Unsure', value: 'UNKNOWN' as HazardAnswer },
];

export type HazardInputRow = {
  id: string;
  name: string;
  description?: string | null;
  hazardLevel: 'LOW' | 'HIGH';
  answer: HazardAnswer | undefined;
  protectionAnswer: HazardAnswer | undefined;
  selectedAssemblyId: string | null | undefined;
};

type InputColumnOptions = {
  assemblies: Assembly[];
  showProtection: boolean;
  isInteractive: boolean;
  setHazardAnswer: (id: string, value: HazardAnswer | undefined) => void;
  setProtectionAnswer: (id: string, value: HazardAnswer | undefined) => void;
  setAssemblyId: (id: string, assemblyId: string | null) => void;
};

export function makeHazardInputColumns({
  assemblies,
  showProtection,
  isInteractive,
  setHazardAnswer,
  setProtectionAnswer,
  setAssemblyId,
}: InputColumnOptions) {
  const cols = [
    {
      id: 'hazard',
      header: 'Hazard',
      // width: 200,
      stringAccessor: (row: HazardInputRow) => row.name,
      accessor: (row: HazardInputRow) => (
        <Box>
          <Text fontWeight="bold" fontSize={1}>
            {row.name}
          </Text>
          {row.description && (
            <Text fontSize={0} color="gray.600" mt={1}>
              {row.description}
            </Text>
          )}
        </Box>
      ),
    },
    {
      id: 'present',
      header: 'Present',
      width: 156 + (showProtection ? 0 : 12),
      stringAccessor: (row: HazardInputRow) => row.answer ?? '',
      accessor: (row: HazardInputRow) => (
        <ButtonToggle
          required={isInteractive}
          disabled={!isInteractive}
          name={`hazard-${row.id}`}
          value={row.answer}
          buttonOptions={HAZARD_OPTIONS.map((opt) => ({
            ...opt,
            onClick: isInteractive
              ? () => {
                  setHazardAnswer(row.id, opt.value);
                  if (opt.value !== 'YES') {
                    setProtectionAnswer(row.id, undefined);
                    setAssemblyId(row.id, null);
                  }
                }
              : undefined,
          }))}
        />
      ),
    },
  ];

  if (showProtection) {
    cols.push({
      id: 'protected',
      header: 'Protected',
      width: assemblies.length > 0 ? 250 : 168,
      stringAccessor: (row: HazardInputRow) => row.protectionAnswer ?? '',
      accessor: (row: HazardInputRow) => {
        if (row.answer !== 'YES') return <></>;
        const selectedAssembly = assemblies.find(
          (a) => String(a.id) === String(row.selectedAssemblyId)
        );
        return (
          <Box>
            <ButtonToggle
              required={isInteractive}
              disabled={!isInteractive}
              name={`protected-${row.id}`}
              value={row.protectionAnswer}
              buttonOptions={HAZARD_OPTIONS.map((opt) => ({
                ...opt,
                onClick: isInteractive
                  ? () => {
                      setProtectionAnswer(row.id, opt.value);
                      if (opt.value !== 'YES') {
                        setAssemblyId(row.id, null);
                      }
                    }
                  : undefined,
              }))}
            />
            {row.protectionAnswer === 'YES' && assemblies.length > 0 && (
              <Box mt={1}>
                <ReactSelect
                  // menuIsOpen
                  options={assemblies}
                  getOptionValue={(a: any) => a.id}
                  getOptionLabel={(a: any) =>
                    [
                      a.model?.backflowAssemblyMake?.name,
                      a.model?.name,
                      a.serialNumber,
                    ]
                      .filter(Boolean)
                      .join(' ')
                  }
                  formatOptionLabel={(a: any) => (
                    <Flex py={1} flexDirection="column">
                      <Text
                        fontSize={1}
                        fontWeight="bold"
                        sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                      >
                        {a.model?.backflowAssemblyMake?.name} {a.model?.name}
                      </Text>
                      <Text
                        fontSize={0}
                        color="gray.500"
                        sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                      >
                        {[a.serialNumber, a.type, a.location]
                          .filter(Boolean)
                          .join(' · ')}
                      </Text>
                    </Flex>
                  )}
                  value={selectedAssembly ?? null}
                  onChange={
                    isInteractive
                      ? (a: any) => setAssemblyId(row.id, a ? a.id : null)
                      : undefined
                  }
                  styles={{
                    container: (base) => ({ ...base, width: '100%' }),
                    menu: (base) => ({ ...base, minWidth: '300px' }),
                  }}
                  isDisabled={!isInteractive}
                  isClearable
                  placeholder="By assembly…"
                />
              </Box>
            )}
          </Box>
        );
      },
    });
  }

  return cols;
}

export function makeHazardResultColumns(assemblies: Assembly[] = []) {
  return [
    {
      id: 'hazard',
      header: 'Hazard',
      accessor: (row: HazardResultRow) => (
        <Flex
          sx={{
            gap: 1,
            textDecoration: row.removed ? 'line-through' : undefined,
            opacity: row.removed ? 0.5 : undefined,
          }}
        >
          <StatusDot
            status={row.hazardLevel === 'HIGH' ? 'error' : 'warning'}
          />
          {row.name}
        </Flex>
      ),
      stringAccessor: (row: HazardResultRow) => row.name,
    },
    {
      id: 'present',
      header: 'Present',
      width: 80,
      stringAccessor: (row: HazardResultRow) => row.answer ?? '',
      accessor: (row: HazardResultRow) => {
        const answer = row.answer;
        if (!answer) return undefined;
        return (
          <StatusChip
            status={
              answer === 'YES'
                ? 'error'
                : answer === 'NO'
                  ? 'success'
                  : 'default'
            }
          >
            {answer === 'YES' ? 'Yes' : answer === 'NO' ? 'No' : 'Unsure'}
          </StatusChip>
        );
      },
    },
    {
      id: 'protected',
      header: 'Protected',
      // width: 250,
      stringAccessor: (row: HazardResultRow) => row.isProtected ?? '',
      accessor: (row: HazardResultRow) => {
        if (row.answer !== 'YES') return <></>;
        const isProtected = row.isProtected;
        const assembly = assemblies.find(
          (a) => String(a.id) === String(row.backflowAssemblyId)
        );
        return (
          <Flex alignItems="center" sx={{ gap: 1 }}>
            <StatusChip
              status={
                isProtected === 'YES'
                  ? 'success'
                  : isProtected === 'NO'
                    ? 'error'
                    : 'default'
              }
            >
              {isProtected === 'YES'
                ? 'Yes'
                : isProtected === 'NO'
                  ? 'No'
                  : 'Unsure'}
            </StatusChip>
            {assembly && (
              <Text fontSize={0} color="gray.500">
                {assembly.model?.backflowAssemblyMake?.name}{' '}
                {assembly.model?.name}
                {assembly.serialNumber ? ` #${assembly.serialNumber}` : ''}
              </Text>
            )}
          </Flex>
        );
      },
    },
  ];
}
